import React from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Loader2, Send, Calendar, MapPin, Bell, Briefcase, Plus, Clock } from 'lucide-react';
import { formatTimeRange, formatDateMonthDay } from '../../../utils/timeFormat';

// Create Group Dialog
export const CreateGroupDialog = ({ 
  open, 
  onOpenChange, 
  newGroupName, 
  setNewGroupName, 
  newGroupDesc, 
  setNewGroupDesc, 
  createGroup 
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="text-gray-900">Create Talent Group</DialogTitle>
        <DialogDescription>
          Organize your favorite talent into groups for quick invitations.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label className="text-gray-700">Group Name</Label>
          <Input
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="e.g., VIP Bartenders"
            className="mt-1"
            data-testid="group-name-input"
          />
        </div>
        <div>
          <Label className="text-gray-700">Description (optional)</Label>
          <Input
            value={newGroupDesc}
            onChange={(e) => setNewGroupDesc(e.target.value)}
            placeholder="Notes about this group"
            className="mt-1"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button 
          onClick={createGroup} 
          className="bg-[#03DFFC] hover:bg-[#03DFFC]/90 text-gray-900"
          data-testid="create-group-submit"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Group
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Invite to Job Dialog
export const InviteDialog = ({
  open,
  onOpenChange,
  inviteMode,
  shifts,
  selectedShifts,
  setSelectedShifts,
  inviteMessage,
  setInviteMessage,
  sendInvites,
  inviting,
  selectedTalentCount,
  favoritesCount
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="text-gray-900 flex items-center gap-2">
          <Send className="w-5 h-5 text-[#FF2D95]" />
          Invite to Job
        </DialogTitle>
        <DialogDescription>
          {inviteMode === 'favorites' && `Invite all ${favoritesCount} favorites to a gig`}
          {inviteMode === 'selected' && `Invite ${selectedTalentCount} selected talent to a gig`}
          {inviteMode === 'group' && 'Invite all group members to a gig'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label className="text-gray-700">Select Shift(s)</Label>
          <div className="mt-2 max-h-48 overflow-y-auto space-y-2">
            {shifts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No open shifts available</p>
            ) : (
              shifts.map(shift => (
                <label 
                  key={shift.id}
                  className={`flex items-center p-3 rounded-lg cursor-pointer border transition-colors ${
                    selectedShifts.includes(shift.id)
                      ? 'bg-[#FF2D95]/5 border-[#FF2D95]'
                      : 'bg-gray-50 border-transparent hover:border-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedShifts.includes(shift.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedShifts([...selectedShifts, shift.id]);
                      } else {
                        setSelectedShifts(selectedShifts.filter(id => id !== shift.id));
                      }
                    }}
                    className="mr-3 accent-[#FF2D95]"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{shift.venue_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDateMonthDay(shift.shift_date)}
                      </span>
                      <span>{formatTimeRange(shift.start_time, shift.end_time)}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {shift.role_name}
                  </Badge>
                </label>
              ))
            )}
          </div>
        </div>
        <div>
          <Label className="text-gray-700">Personal Message (optional)</Label>
          <Textarea
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            placeholder="Add a note to your invitation..."
            className="mt-1 resize-none"
            rows={3}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button 
          onClick={sendInvites}
          disabled={selectedShifts.length === 0 || inviting}
          className="bg-[#FF2D95] hover:bg-[#FF2D95]/90 text-white"
          data-testid="send-invites-btn"
        >
          {inviting ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Send Invitations
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Priority Notification Dialog
export const PriorityDialog = ({
  open,
  onOpenChange,
  shifts,
  priorityShiftId,
  setPriorityShiftId,
  priorityWindowHours,
  setPriorityWindowHours,
  sendPriorityNotification,
  sendingPriority,
  favoritesCount
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="text-gray-900 flex items-center gap-2">
          <Bell className="w-5 h-5 text-yellow-500" />
          Priority Notification
        </DialogTitle>
        <DialogDescription>
          Send urgent notifications to all {favoritesCount} favorites for early gig access.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label className="text-gray-700">Select Gig</Label>
          <Select value={priorityShiftId} onValueChange={setPriorityShiftId}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Choose a gig..." />
            </SelectTrigger>
            <SelectContent>
              {shifts.map(shift => (
                <SelectItem key={shift.id} value={shift.id}>
                  {shift.venue_name} - {formatDateMonthDay(shift.shift_date)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-gray-700">Priority Window (hours)</Label>
          <Input
            type="number"
            min="1"
            max="24"
            value={priorityWindowHours}
            onChange={(e) => setPriorityWindowHours(parseInt(e.target.value) || 4)}
            className="mt-1"
          />
          <p className="text-xs text-gray-500 mt-1">
            Favorites get exclusive access for this duration before the gig opens to all talent.
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button 
          onClick={sendPriorityNotification}
          disabled={!priorityShiftId || sendingPriority}
          className="bg-yellow-500 hover:bg-yellow-600 text-white"
        >
          {sendingPriority ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Bell className="w-4 h-4 mr-2" />
          )}
          Send Priority Alert
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Direct Book Dialog
export const DirectBookDialog = ({
  open,
  onOpenChange,
  shifts,
  directBookShiftId,
  setDirectBookShiftId,
  directBookTalentIds,
  handleDirectBook,
  directBooking
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="text-gray-900 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-green-600" />
          Direct Book Talent
        </DialogTitle>
        <DialogDescription>
          Instantly book {directBookTalentIds.length} talent without waiting for them to apply.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label className="text-gray-700">Select Gig</Label>
          <Select value={directBookShiftId} onValueChange={setDirectBookShiftId}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Choose a gig..." />
            </SelectTrigger>
            <SelectContent>
              {shifts.map(shift => (
                <SelectItem key={shift.id} value={shift.id}>
                  {shift.venue_name} - {formatDateMonthDay(shift.shift_date)} ({shift.role_name})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            Direct booking will immediately confirm these talent for the selected gig. 
            They will be notified of their booking.
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button 
          onClick={handleDirectBook}
          disabled={!directBookShiftId || directBooking}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          {directBooking ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Briefcase className="w-4 h-4 mr-2" />
          )}
          Confirm Booking
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
